/**
 * webSpaceCollectiblesDraw.ts
 * Canvas2D draw helper for World 2 collectibles (Constellation Shards
 * and Star Seed loot). Extracted from `webSpaceDraw.ts` so the parent
 * file stays under the AGENTS.md 300-line cap.
 *
 * Renders the actual PixelLab Star Seed / Constellation Shard sprites
 * (same assets used by the World 1 platformer) scaled down to glints
 * (20px shard / 14px loot from 48x48 and 44x40 source crops) so they
 * read as collectibles without dominating the busy space playfield.
 * Falls back to a procedural 5-point star while the sprite decode is
 * still in flight — sized from `drawSize` so the swap to the sprite
 * is the same on-screen size and the player doesn't see a visible
 * size pop on decode.
 *
 * @module webSpaceCollectiblesDraw
 */

/// <reference lib="dom" />

import type { SpaceDrawList } from './spaceRender';
import type { SpaceRect } from './spaceRenderTypes';
import spaceShardUrl from '../../assets/sprites/space-shard.png';
import starSeedUrl from '../../assets/sprites/star-seed.png';
import {
  type ImageSlot,
  createImageSlot,
  ensureImageLoaded,
} from './lazyImageLoader';

const CONSTELLATION_SHARD_MODULE: string = spaceShardUrl;
const STAR_SEED_MODULE: string = starSeedUrl;

// DECISION: Per-sprite visible source rect. PixelLab bakes a label
// area into the bottom rows AND a vertical marker line into the
// right column of the W1 `star-seed.png` sprite. The marker column
// (col 44, full height) reads as a black vertical stripe on the
// right edge of loot drops at runtime, so the loot crop must exclude
// it. The W2 `space-shard.png` is a clean full-canvas PixelLab
// render with no label or marker — full 48x48 source rect.
const SHARD_SRC_VISIBLE_WIDTH = 48;
const SHARD_SRC_VISIBLE_HEIGHT = 48;
const LOOT_SRC_VISIBLE_WIDTH = 44;
const LOOT_SRC_VISIBLE_HEIGHT = 40;

// Visual draw size in viewport px. Sized so the collectible reads
// against the busy playfield without dominating it; tuned 50%
// smaller than the first sprite-path pass so shards and loot drops
// feel like glints rather than chunky pickups.
const SHARD_DRAW_SIZE = 20;
const LOOT_DRAW_SIZE = 14;

const STAR_POINTS = 5;
const STAR_INNER_RATIO = 0.45;

const shardSpriteSlot: ImageSlot = createImageSlot();
const lootSpriteSlot: ImageSlot = createImageSlot();

/**
 * Test-only hook: install a decoded shard sprite. `null` clears.
 *
 * @param img - Decoded sprite, or `null` to clear.
 * @returns void — mutates module-local cache state.
 */
export function _setShardSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  shardSpriteSlot.current = img;
  shardSpriteSlot.loadStarted = img !== null;
}

/**
 * Test-only hook: install a decoded loot sprite. `null` clears.
 *
 * @param img - Decoded sprite, or `null` to clear.
 * @returns void — mutates module-local cache state.
 */
export function _setLootSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  lootSpriteSlot.current = img;
  lootSpriteSlot.loadStarted = img !== null;
}

/**
 * Trace a 5-point star path centred in the given rect. Used only as
 * a fallback while the sprite decode is still in flight.
 *
 * @param ctx - 2D rendering context.
 * @param rect - Bounding rect (the star inscribes the rect).
 * @returns void — mutates the active path on `ctx`.
 */
function traceStarPath(ctx: CanvasRenderingContext2D, rect: SpaceRect): void {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const outerR = Math.min(rect.width, rect.height) / 2;
  const innerR = outerR * STAR_INNER_RATIO;
  const vertexCount = STAR_POINTS * 2;
  for (let i = 0; i < vertexCount; i += 1) {
    const angle = (i * Math.PI) / STAR_POINTS - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    const px = cx + Math.cos(angle) * r;
    const py = cy + Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

/**
 * Paint one collectible using its sprite when decoded, falling back to
 * a procedural 5-point star otherwise. Sprite is drawn cropped to its
 * caller-supplied visible source rect (excluding any PixelLab-baked
 * label rows / marker columns) and centred on the rect midpoint at
 * the caller-supplied draw size.
 *
 * @param ctx - 2D rendering context.
 * @param rect - Per-frame rect (camera-relative position + colour).
 * @param sprite - Decoded sprite, or `null` while decode is pending.
 * @param drawSize - Visual draw size in viewport px (square).
 * @param srcVisibleWidth - Left N source columns that contain art
 *   (the remaining columns hold a PixelLab marker line and must be
 *   cropped out).
 * @param srcVisibleHeight - Top N source rows that contain art.
 * @returns void — paints directly onto `ctx`.
 */
function drawCollectible(
  ctx: CanvasRenderingContext2D,
  rect: SpaceRect,
  sprite: globalThis.HTMLImageElement | null,
  drawSize: number,
  srcVisibleWidth: number,
  srcVisibleHeight: number,
): void {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  if (sprite !== null) {
    ctx.drawImage(
      sprite,
      0,
      0,
      srcVisibleWidth,
      srcVisibleHeight,
      cx - drawSize / 2,
      cy - drawSize / 2,
      drawSize,
      drawSize,
    );
    return;
  }
  // DECISION: size the fallback star from `drawSize`, not the per-
  // frame hitbox rect. The hitbox is larger than the sprite (e.g.
  // 28x28 vs 20x20 for shards) so tracing the star against the rect
  // would make the fallback noticeably larger than the eventual
  // sprite — a visible size pop on decode.
  const fallbackRect: SpaceRect = {
    ...rect,
    x: cx - drawSize / 2,
    y: cy - drawSize / 2,
    width: drawSize,
    height: drawSize,
  };
  ctx.beginPath();
  traceStarPath(ctx, fallbackRect);
  ctx.fillStyle = rect.color;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.stroke();
}

/**
 * Paint the collectibles band of the draw list — Constellation Shards
 * first, then Star Seed loot drops. Both layers share the same draw
 * primitive so a shard and a loot drop in the same frame have visually
 * consistent rendering (only the source sprite differs).
 *
 * @param ctx - 2D rendering context.
 * @param drawList - Per-frame draw list from {@link buildSpaceDrawList}.
 * @returns void — paints directly onto `ctx`.
 */
export function drawSpaceCollectiblesLayer(
  ctx: CanvasRenderingContext2D,
  drawList: SpaceDrawList,
): void {
  ensureImageLoaded(shardSpriteSlot, CONSTELLATION_SHARD_MODULE);
  ensureImageLoaded(lootSpriteSlot, STAR_SEED_MODULE);
  for (const s of drawList.shards) {
    drawCollectible(
      ctx,
      s,
      shardSpriteSlot.current,
      SHARD_DRAW_SIZE,
      SHARD_SRC_VISIBLE_WIDTH,
      SHARD_SRC_VISIBLE_HEIGHT,
    );
  }
  for (const s of drawList.loot) {
    drawCollectible(
      ctx,
      s,
      lootSpriteSlot.current,
      LOOT_DRAW_SIZE,
      LOOT_SRC_VISIBLE_WIDTH,
      LOOT_SRC_VISIBLE_HEIGHT,
    );
  }
}
